<template>
  <div class="section">
    <h4 class="section-title">
      {{ $t('Account Settings') }}
    </h4>
    <p class="divider small"></p>
    <div class="form-section" ref="refFormSection" :class="{ mobile: formSectionWidth <  580 }">
      <template v-if="isCompanyAdmin && !isSSO">
        <div class="flex-row flex-align-center flex-justify-between form-section-item">
          <div class="flex-row flex-1 flex-align-center">
            <img class="icon" src="@/assets/icon-setting-company.svg" v-svg-inline alt="">
            <div class="form-title">
              <h5>{{ $t('Enterprise management') }}</h5>
              <p class="form-desc">{{ $t('Flexible configuration, tracking, and management of licenses within your company.') }}</p>
            </div>
          </div>
          <el-button class="btn" @click="handleGoToRoute('companyManagement')">{{ $t('Enterprise management dashboard') }}</el-button> 
        </div>
        <p class="divider"></p>
      </template>
    
      <div class="flex-row flex-align-center flex-justify-between form-section-item">
        <div class="flex-row flex-1 flex-align-center">
          <img class="icon" src="@/assets/icon-setting-password.svg" v-svg-inline alt="">
          <div class="form-title">
            <h5>{{ $t('Password') }}</h5>
            <span class="form-desc">{{ $t('You can set password if you don\'t prefer logging in with codes.') }}</span>
          </div>
        </div>
        <el-button class="btn" :disabled="!userStore.canChangePassword" @click="handleShowChangePassword">{{ $t('Change') }}</el-button>  
      </div>
      <template v-if="userStore.canChangePassword">
        <AuthAccount ref="refAuthAccount" auth-type="resetPass" :can-change-account-type="true" @on-success="handleCheckChangePasswordCodeSuccess"></AuthAccount>
        <ChangePassword ref="refChangePassword" :code-id="resetPassCodeId" :code="resetPassCode" :account="resetPassAccount" @on-success="handleChangePassSuccess"></ChangePassword>
      </template>
      <p class="divider"></p>

      <div class="flex-row flex-align-center flex-justify-between form-section-item">
        <div class="flex-row flex-1 flex-align-center">
          <img class="icon" src="@/assets/icon-setting-logout.svg" v-svg-inline alt="">
          <div class="form-title">
            <h5>{{ $t('Log Out') }}</h5>
            <span class="form-desc">{{ $t('You may not be able to use all features after logging out of your account until you log back in.') }}</span>
          </div>
        </div>
        <el-button class="btn" @click="handleLogout()" :loading="logoutBtnLoading">{{ $t('Log Out') }}</el-button>  
      </div>
      
      <template v-if="!isSSO">
        <p class="divider"></p>
        <div class="flex-row flex-align-center flex-justify-between form-section-item">
          <div class="flex-row flex-1 flex-align-center">
            <img class="icon" src="@/assets/icon-setting-delete.svg" v-svg-inline alt="">
            <div class="form-title">
              <h5>{{ $t('Delete your account') }}</h5>
              <span class="form-desc">{{ $t('Your personal information and all data will be removed permanently and can\'t be recovered.') }}</span>
            </div>
          </div>
          <el-button class="btn" @click="handleDeleteAccount">{{ $t('Delete') }}</el-button>
        </div>
        <DeleteAccount ref="refDeleteAccount" :is-company-admin="isCompanyAdmin"></DeleteAccount>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { appStore, userStore } from '@/stores';
import { useRouter } from 'vue-router';
import ChangePassword from '../components/change-password.vue';
import AuthAccount from '../components/auth-account.vue';
import { logOut } from '@/api/user/user';
import { logOutSuccess } from '@/services/login';
import DeleteAccount from '../components/delete-account.vue';
import { useElementSize } from '@vueuse/core';

const $router = useRouter()

const refFormSection = ref()
const { width: formSectionWidth } = useElementSize(refFormSection)

const userInfo = computed(() => userStore.userInfo)
// 是否是sso登录
const isSSO = computed(() => userInfo.value?.isSSO)
// 是否超级管理员
const isCompanyAdmin = computed(() => userInfo.value?.role === 3 || userInfo.value?.role === 1)

// 跳转到指定路由
const handleGoToRoute = routeName => {
  if (appStore.isIframe) {
    const route = $router.resolve({ name: routeName })
    window.open(route.href, '_blank')
  } else {
    $router.push({ name: routeName })
  }
}

// 修改密码前验证码弹窗
const refAuthAccount = ref(null)
const handleShowChangePassword = () => {
  let accountType = 'email', account = userInfo.value?.email
  // 如果是国内用户并且有绑定手机号，则优先验证手机号
  if (appStore.isCN && userInfo.value?.phone) {
    accountType = 'phone'
    account = userInfo.value?.phone
  }
  refAuthAccount.value.open(accountType, account)
}

// 修改密码弹窗
const refChangePassword = ref(null)
const resetPassCodeId = ref('')
const resetPassCode = ref(0)
const resetPassAccount = ref('')
const handleCheckChangePasswordCodeSuccess = (codeId, code, account) => {
  resetPassCode.value = code
  resetPassCodeId.value = codeId
  resetPassAccount.value = account
  refChangePassword.value.show()
}
// 修改密码成功后自动退出
const handleChangePassSuccess = () => {
  logOutSuccess()
}

// 退出登录
const logoutBtnLoading = ref(false)
const handleLogout = () => {
  logoutBtnLoading.value = true
  logOut().finally(() => {
    logOutSuccess(false)
  })
}

// 删除账号
const refDeleteAccount = ref(null)
const handleDeleteAccount = async () => {
  refDeleteAccount.value.showUserAuth()
}

onMounted(() =>{
})
</script>

<style lang="scss" scoped>
.form-title {
  flex: 1;
}
.icon {
  width: 32px;
  height: 32px;
  margin-right: 12px;
}
.btn {
  min-width: 100px;
  margin-left: 24px;
}
.form-section.mobile {
  .form-section-item {
    flex-direction: column;
    align-items: start;
    .icon {
      width: 24px;
      height: 24px;
    }
    .btn {
      margin-left: 32px;
      margin-top: 12px;
    }
  }
}
@media screen and (max-width: 400px) {
  .btn {
    zoom: 0.8;
  }
}
</style>
<style lang="scss">
.ar-SA {
  .icon {
    margin-right: 0;
    margin-left: 12px;
  }
}
</style>